import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, TrendingUp, LayoutDashboard, LogOut } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser, selectIsAuthenticated } from "../../../redux";
import { removeToken } from "../../../utils/tokenHelper";
import { useGlobalToast } from "../../../context/ToastContext";
import styles from "./LandingLayout.module.scss";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Features", path: "/features" },
  { label: "Pricing", path: "/pricing" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const LandingLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const { showSuccess } = useGlobalToast();
  
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    removeToken();
    dispatch(logoutUser());
    showSuccess("Successfully logged out!");
    navigate("/", { replace: true });
  };

  return (
    <div className={styles.layout}>
      {/* Navigation */}
      <header
        className={`${styles.header} ${isScrolled ? styles.scrolled : ""}`}
      >
        <div className={styles.container}>
          <Link to="/" className={styles.logo}>
            <img src="/assets/images/logo-text.svg" alt="BizAura Logo" className={styles.logoImg} />
          </Link>

          {/* Desktop Nav */}
          <nav className={styles.desktopNav}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`${styles.navLink} ${
                  location.pathname === link.path ? styles.active : ""
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons */}
          <div className={styles.authButtons}>
            {isAuthenticated ? (
              <>
                <Link to="/app/dashboard" className={styles.dashboardBtn}>
                  <LayoutDashboard size={18} />
                  Dashboard
                </Link>
                <button 
                  onClick={handleLogout} 
                  className={styles.logoutBtn}
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className={styles.signupBtn}>
                Get Started
              </Link>
            )}
          </div>

          {/* Mobile Toggle */}
          <button
            className={styles.mobileToggle}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className={styles.mobileMenu}>
            <nav className={styles.mobileNav}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`${styles.mobileNavLink} ${
                    location.pathname === link.path ? styles.active : ""
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className={styles.mobileAuth}>
              {isAuthenticated ? (
                <>
                  <Link to="/app/dashboard" className={styles.mobileDashboardBtn}>
                    Dashboard
                  </Link>
                  <button 
                    onClick={handleLogout} 
                    className={styles.mobileLogoutBtn}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" className={styles.mobileSignupBtn}>
                  Get Started
                </Link>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className={styles.main}>{children}</main>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerTop}>
            <div className={styles.footerBrand}>
              <div className={styles.footerLogo}>
                <img src="/assets/images/logo-text-light.svg" alt="BizAura Logo" className={styles.footerLogoImg} />
              </div>
              <p className={styles.footerDescription}>
                The all-in-one CRM platform for small businesses. Manage
                clients, orders, and payments in one simple dashboard.
              </p>
            </div>
            <div className={styles.footerLinks}>
              <div className={styles.footerColumn}>
                <h4>Product</h4>
                <ul>
                  <li>
                    <Link to="/features">Features</Link>
                  </li>
                  <li>
                    <Link to="/pricing">Pricing</Link>
                  </li>
                  <li>
                    <Link to="/">Integrations</Link>
                  </li>
                  <li>
                    <Link to="/">API</Link>
                  </li>
                </ul>
              </div>
              <div className={styles.footerColumn}>
                <h4>Company</h4>
                <ul>
                  <li>
                    <Link to="/about">About</Link>
                  </li>
                  <li>
                    <Link to="/">Blog</Link>
                  </li>
                  <li>
                    <Link to="/">Careers</Link>
                  </li>
                  <li>
                    <Link to="/contact">Contact</Link>
                  </li>
                </ul>
              </div>
              <div className={styles.footerColumn}>
                <h4>Resources</h4>
                <ul>
                  <li>
                    <Link to="/policy/HelpAndFAQ">HelpAndFAQ</Link>
                  </li>
                  <li>
                    <Link to="/contact">ContactUs</Link>
                  </li>
                  <li>
                    <Link to="/policy/AdvertiseWithUs">AdvertiseWithUs</Link>
                  </li>
                </ul>
              </div>
              <div className={styles.footerColumn}>
                <h4>Legal</h4>
                <ul>
                  <li>
                    <Link to="/policy/PrivacyPolicy">PrivacyPolicy</Link>
                  </li>
                  <li>
                    <Link to="/policy/TermsAndConditions">
                      TermsAndConditions
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className={styles.footerBottom}>
            <p>© 2024 BizAura. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingLayout;
